/**
 * Uvjetna vidljivost pitanja i sekcija upitnika (Irenina tocka 7).
 *
 * Zrcalo od app/Support/Kyc/QuestionVisibility.php. Gramatika mora ostati ista u
 * oba: backend na njoj racuna sto je obavezno pri predaji predmeta, frontend na
 * njoj odlucuje sto uopce prikazati. Ako se raziđu, stranka vidi pitanje koje
 * backend ne trazi, ili joj predaja puca na pitanju koje nikad nije vidjela.
 *
 *   { key: 'je_pep', equals: 'da' }
 *   { key: 'izvor_sredstava', in: ['Ostalo'] }
 *   { key: 'nastupa_u_svoje_ime', not_equals: 'da' }
 *   { key: 'izvor_sredstava', answered: true }
 *   { field: 'subject_type', in: ['pravna', 'obrt'] }
 *   { field: 'transaction_amount_eur', gte: 10000 }
 *   { all: [...] } / { any: [...] } / { not: {...} }
 *
 * Nepoznat oblik uvjeta znaci VIDLJIVO - isto kao u PHP-u.
 */

export type VisibilityCondition = {
    key?: string;
    field?: string;
    equals?: unknown;
    not_equals?: unknown;
    in?: unknown[];
    answered?: boolean;
    gte?: number;
    lt?: number;
    all?: VisibilityCondition[];
    any?: VisibilityCondition[];
    not?: VisibilityCondition;
};

export type VisibilityContext = Record<string, unknown>;
export type AnswerMap = Record<string, unknown>;

const looseEquals = (a: unknown, b: unknown): boolean => {
    if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
    if (a === null || a === undefined || b === null || b === undefined) return a === b;
    if (typeof a === 'object' || typeof b === 'object') return a === b;
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
};

function compare(value: unknown, condition: VisibilityCondition): boolean {
    if (condition.answered !== undefined) {
        const answered = value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
        return answered === Boolean(condition.answered);
    }
    if ('equals' in condition) return looseEquals(value, condition.equals);
    if ('not_equals' in condition) return !looseEquals(value, condition.not_equals);
    if (Array.isArray(condition.in)) {
        // Visestruki odabir (t.8): odgovor moze biti niz.
        const values = Array.isArray(value) ? value : [value];
        return values.some((v) => condition.in!.some((c) => looseEquals(v, c)));
    }
    if (condition.gte !== undefined) {
        return value !== null && value !== undefined && value !== '' && Number(value) >= condition.gte;
    }
    if (condition.lt !== undefined) {
        return value !== null && value !== undefined && value !== '' && Number(value) < condition.lt;
    }
    return true;
}

function evaluate(condition: VisibilityCondition, answers: AnswerMap, context: VisibilityContext): boolean {
    if (Array.isArray(condition.all)) return condition.all.every((c) => evaluate(c, answers, context));
    if (Array.isArray(condition.any)) return condition.any.some((c) => evaluate(c, answers, context));
    if (condition.not) return !evaluate(condition.not, answers, context);
    if (condition.key !== undefined) return compare(answers[condition.key], condition);
    if (condition.field !== undefined) return compare(context[condition.field], condition);
    return true;
}

export function isVisible(node: { visible_if?: VisibilityCondition }, answers: AnswerMap, context: VisibilityContext = {}): boolean {
    const condition = node.visible_if;
    if (!condition || typeof condition !== 'object' || Object.keys(condition).length === 0) return true;
    return evaluate(condition, answers, context);
}

/** Pitanja sekcije koja su trenutno vidljiva; prazno ako je i sama sekcija skrivena. */
export function visibleQuestions<Q extends { visible_if?: VisibilityCondition }>(
    section: { visible_if?: VisibilityCondition; questions?: Q[] },
    answers: AnswerMap,
    context: VisibilityContext = {},
): Q[] {
    if (!isVisible(section, answers, context)) return [];
    return (section.questions ?? []).filter((q) => isVisible(q, answers, context));
}
